import { useState } from "react";
import { formatSEK } from "../lib/format";

interface BarData {
  label: string; // YYYY-MM
  spending: number;
  income: number;
}

interface BarChartProps {
  data: BarData[];
  title?: string;
  /** Total monthly budget — rendered as a marker on each month's spending bar */
  budgetTotal?: number;
}

const MONTH_NAMES = [
  "jan", "feb", "mar", "apr", "maj", "jun",
  "jul", "aug", "sep", "okt", "nov", "dec",
];

function formatCompact(val: number): string {
  const sign = val < 0 ? "-" : "";
  const abs = Math.abs(val);
  if (abs >= 1_000_000) return `${sign}${(abs / 1_000_000).toFixed(1)}M`;
  if (abs >= 1000) return `${sign}${(abs / 1000).toFixed(0)}k`;
  return `${sign}${abs.toFixed(0)}`;
}

function formatMonthLabel(label: string): { short: string; long: string } | null {
  const match = label.match(/^(\d{4})-(\d{2})$/);
  if (!match?.[1] || !match[2]) return null;

  const year = Number.parseInt(match[1], 10);
  const month = Number.parseInt(match[2], 10);
  if (!Number.isFinite(year) || month < 1 || month > 12) return null;

  const monthName = MONTH_NAMES[month - 1];
  if (!monthName) return null;

  return {
    short: `${monthName} ${String(year).slice(2)}`,
    long: `${monthName} ${year}`,
  };
}

function pct(value: number, max: number): number {
  if (!Number.isFinite(value) || value <= 0 || max <= 0) return 0;
  return Math.max((value / max) * 100, 1.5);
}

export function BarChart({ data, title, budgetTotal }: BarChartProps) {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const rows = data
    .map((d) => ({ ...d, monthLabel: formatMonthLabel(d.label) }))
    .filter((d): d is BarData & { monthLabel: { short: string; long: string } } => d.monthLabel !== null);

  if (rows.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 text-muted-foreground">
        Ingen månadsdata
      </div>
    );
  }

  const maxVal = Math.max(
    ...rows.map((x) => Math.max(x.spending, x.income)),
    budgetTotal ?? 0,
    1
  );
  const budgetPct =
    budgetTotal && budgetTotal > 0 ? (budgetTotal / maxVal) * 100 : null;

  return (
    <div className="space-y-4">
      {title && <h3 className="font-semibold text-lg">{title}</h3>}

      {/* Legend */}
      <div className="flex items-center gap-4 text-xs text-muted-foreground">
        <div className="flex items-center gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-destructive/80" />
          Utgifter
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-success/80" />
          Inkomster
        </div>
        {budgetPct !== null && (
          <div className="flex items-center gap-1.5">
            <div className="w-0.5 h-3 rounded-full bg-foreground/60" />
            Budget {formatSEK(budgetTotal!)}
          </div>
        )}
      </div>

      <div className="space-y-2">
        {rows.map((d, i) => {
          const isActive = activeIndex === i;
          const net = d.income - d.spending;
          const spendingPct = pct(d.spending, maxVal);
          const incomePct = pct(d.income, maxVal);

          return (
            <div
              key={d.label}
              className={`group rounded-xl px-3 py-3 transition-colors ${
                isActive ? "bg-muted/70" : "hover:bg-muted/40"
              }`}
              onMouseEnter={() => setActiveIndex(i)}
              onMouseLeave={() => setActiveIndex(null)}
              title={`${d.monthLabel.long}: utgifter ${formatSEK(d.spending)}, inkomster ${formatSEK(d.income)}`}
            >
              <div className="grid grid-cols-[4.5rem_minmax(0,1fr)_5.5rem] items-center gap-3">
                <div className="text-xs font-medium text-muted-foreground tabular-nums uppercase">
                  {d.monthLabel.short}
                </div>

                <div className="min-w-0 space-y-2">
                  <div className="grid grid-cols-[minmax(0,1fr)_3.75rem] items-center gap-2">
                    <div className="relative h-2.5 overflow-hidden rounded-full bg-destructive/10">
                      <div
                        className="h-full rounded-full bg-destructive/80 transition-all duration-300"
                        style={{ width: `${spendingPct}%` }}
                      />
                      {budgetPct !== null && (
                        <div
                          className="absolute inset-y-0 w-0.5 bg-foreground/60"
                          style={{ left: `${budgetPct}%` }}
                        />
                      )}
                    </div>
                    <span className="text-right text-[11px] tabular-nums text-muted-foreground">
                      {formatCompact(d.spending)}
                    </span>
                  </div>

                  <div className="grid grid-cols-[minmax(0,1fr)_3.75rem] items-center gap-2">
                    <div className="h-2.5 overflow-hidden rounded-full bg-success/10">
                      <div
                        className="h-full rounded-full bg-success/80 transition-all duration-300"
                        style={{ width: `${incomePct}%` }}
                      />
                    </div>
                    <span className="text-right text-[11px] tabular-nums text-muted-foreground">
                      {formatCompact(d.income)}
                    </span>
                  </div>
                </div>

                <div
                  className={`text-right text-sm font-semibold tabular-nums ${
                    net >= 0 ? "text-success" : "text-destructive"
                  }`}
                >
                  {net >= 0 ? "+" : ""}
                  {formatCompact(net)}
                </div>
              </div>

              {isActive && (
                <div className={`mt-2 grid ${budgetPct !== null ? "grid-cols-4" : "grid-cols-3"} gap-2 pl-[5.25rem] text-[11px] text-muted-foreground`}>
                  <span>
                    Utgifter:{" "}
                    <span className="font-medium text-destructive">
                      {formatSEK(d.spending)}
                    </span>
                  </span>
                  <span>
                    Inkomster:{" "}
                    <span className="font-medium text-success">
                      {formatSEK(d.income)}
                    </span>
                  </span>
                  <span>
                    Netto:{" "}
                    <span className={`font-medium ${net >= 0 ? "text-success" : "text-destructive"}`}>
                      {net >= 0 ? "+" : ""}
                      {formatSEK(net)}
                    </span>
                  </span>
                  {budgetPct !== null && (
                    <span>
                      Mot budget:{" "}
                      <span
                        className={`font-medium ${
                          d.spending <= budgetTotal!
                            ? "text-success"
                            : "text-destructive"
                        }`}
                      >
                        {d.spending <= budgetTotal!
                          ? `${formatSEK(budgetTotal! - d.spending)} kvar`
                          : `${formatSEK(d.spending - budgetTotal!)} över`}
                      </span>
                    </span>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
